/**
 * =============================================================================
 * USE SIMILAR QUESTIONS HOOK
 * =============================================================================
 *
 * Hook for fetching similar questions with debounce.
 * Used in QuestionForm to show potential duplicates as user types title.
 *
 * PHASE REFERENCE: PHASE_1_SPEC.md P1-19
 *
 * Features:
 * - 300ms debounce on input changes
 * - Only triggers after 3+ characters
 * - Automatic cleanup on unmount
 * - Loading state management
 * - Error handling
 *
 * =============================================================================
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { widgetApi, type SimilarQuestionItem } from '../api/client';
import { logger } from '../lib/logger';

// =============================================================================
// CONSTANTS
// =============================================================================

/** Minimum characters required before triggering search (P1-19 acceptance criteria) */
const MIN_QUERY_LENGTH = 3;

/** Debounce delay in milliseconds (P1-19 acceptance criteria) */
const DEBOUNCE_MS = 300;

/** Maximum similar questions to fetch */
const MAX_RESULTS = 5;

// =============================================================================
// TYPES
// =============================================================================

export interface UseSimilarQuestionsResult {
  /** Array of similar questions */
  questions: SimilarQuestionItem[];
  /** Whether a search is currently in progress */
  isLoading: boolean;
  /** Error message if search failed */
  error: string | null;
  /** Clear the results */
  clear: () => void;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Hook to fetch similar questions with debounced input.
 *
 * Implements the P1-19 acceptance criteria:
 * - Triggers after 3+ characters AND 300ms debounce
 * - Returns max 5 results
 * - Handles loading and error states
 *
 * @param query - The search query (typically the question title being typed)
 * @returns Similar questions, loading state, and error state
 *
 * @example
 * ```tsx
 * function QuestionForm() {
 *   const [title, setTitle] = useState('');
 *   const { questions, isLoading, error } = useSimilarQuestions(title);
 *
 *   return (
 *     <>
 *       <input value={title} onChange={(e) => setTitle(e.target.value)} />
 *       <SimilarQuestions questions={questions} isLoading={isLoading} />
 *     </>
 *   );
 * }
 * ```
 */
export function useSimilarQuestions(query: string): UseSimilarQuestionsResult {
  const [questions, setQuestions] = useState<SimilarQuestionItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track timeout for cleanup
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Track AbortController for request cancellation (I-H3)
  const abortControllerRef = useRef<AbortController | null>(null);

  /**
   * Clear all results and reset state.
   */
  const clear = useCallback(() => {
    setQuestions([]);
    setError(null);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    // Trim the query for comparison
    const trimmedQuery = query.trim();

    // Clear any pending timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // Abort any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    // Don't search if query is too short
    if (trimmedQuery.length < MIN_QUERY_LENGTH) {
      setQuestions([]);
      setIsLoading(false);
      setError(null);
      return;
    }

    // Set loading state immediately for better UX
    setIsLoading(true);

    // Debounce the search
    timeoutRef.current = setTimeout(async () => {
      // Check if widget is initialized
      if (!widgetApi.isInitialized()) {
        setError('Widget not initialized');
        setIsLoading(false);
        return;
      }

      // Create new AbortController for this request
      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        const response = await widgetApi.questions.findSimilar(
          trimmedQuery,
          MAX_RESULTS,
          { signal: controller.signal }
        );

        // Check if request was aborted
        if (controller.signal.aborted) {
          return;
        }

        if (response.success) {
          setQuestions(response.data.questions);
          setError(null);
        } else {
          // Don't show error to user for search failures - just show empty state
          logger.error(`Similar questions search failed: ${response.error.message}`);
          setQuestions([]);
          setError(null);
        }
      } catch (err) {
        // Ignore abort errors
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }
        // Network errors - silently fail without showing error to user
        logger.error(`Similar questions search error: ${err instanceof Error ? err.message : 'Unknown error'}`);
        if (!controller.signal.aborted) {
          setQuestions([]);
          setError(null);
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    }, DEBOUNCE_MS);

    // Cleanup function
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [query]);

  return {
    questions,
    isLoading,
    error,
    clear,
  };
}
